import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';

import ProtectedData, { getSeverity, isProtectedData, UNKNOWN } from 'pages/components/ProtectedData';
import average from 'utils/average';
import toMeaningfulFixed from 'utils/to-meaningful-fixed';

import ChevronIcon from './assets/chevron-down.svg';
import useStyles from './styles';

interface Props {
    label: React.ReactNode;
    scale: number;
    precision: number;
    data: { score: number | string }[];
    open?: boolean;
    onClick?: () => void;
    className?: string;
}

const Label: React.FC<Props> = ({ open, label, scale, precision, data, onClick, className }) => {
    const { classes, cx } = useStyles();

    const protectedScore = useMemo(() => data.find(({ score }) => isProtectedData(score))?.score, [data]);

    const averageScore = useMemo(
        () => protectedScore || average(...data.map(({ score }) => Number(score))),
        [data, protectedScore]
    );

    const scoreRange = useMemo(() => {
        if (protectedScore) {
            return protectedScore;
        }

        const scores = data.map(({ score }) => Number(score)).filter((score) => Number.isFinite(score));

        if (isEmpty(scores)) {
            return null;
        }

        const min = Math.min(...scores);
        const max = Math.max(...scores);
        return min === max
            ? toMeaningfulFixed(min, precision)
            : `${toMeaningfulFixed(min, precision)} - ${toMeaningfulFixed(max, precision)}`;
    }, [data, precision, protectedScore]);

    const severity = getSeverity(averageScore, scale);

    return (
        <div className={cx(classes.line, className)} onClick={onClick}>
            <span className={classes.label}>{label}</span>
            <span
                className={cx(classes.score, {
                    [classes._green]: severity === 'Low',
                    [classes._orange]: severity === 'Medium',
                    [classes._red]: severity === 'High',
                    [classes._gray]: severity === UNKNOWN
                })}
            >
                {
                    <ProtectedData data={averageScore}>
                        {({ isProtected, content }) => (isProtected ? content : scoreRange)}
                    </ProtectedData>
                }
            </span>
            <ChevronIcon className={cx(classes.chevron, { [classes._rotated]: open })} />
        </div>
    );
};

export default Label;
